export const TV_PROTOCOL_VERSION = 1;
/** Complete current TvGame union (including Start Line, Ladder, and Poker award overlays). */
export const TV_GAME_SCHEMA_CAPABILITY = 'tv.game.schema.v2';
/** App versions that listen for receiver-authenticated, allowlisted commands. */
export const TV_CONTROLLER_COMMAND_CAPABILITY = 'tv.controller.commands.v1';
/** App versions that also execute sub-destination (mode setup) navigations. */
export const TV_CONTROLLER_COMMAND_V2_CAPABILITY = 'tv.controller.commands.v2';
/** App versions that also execute menu-mirror selections and stimp changes. */
export const TV_CONTROLLER_COMMAND_V3_CAPABILITY = 'tv.controller.commands.v3';

export type TvNavigationDestination =
  | 'target'
  | 'challenge'
  | 'profile'
  | 'settings'
  // v2 sub-destinations. Mirror functions/src/handlers/tvDisplay.ts and the
  // app's src/tv/commands.ts — the server allowlist is authoritative.
  | 'challenge-distance'
  | 'challenge-sim9'
  | 'challenge-ladder'
  | 'challenge-startline'
  | 'challenge-poker'
  | 'challenge-linefour'
  | 'challenge-turfwar'
  | 'challenge-ladder1v1';

/** Display metadata is broader than controller navigation for backward
 * compatibility: retired Contour snapshots may finish rendering, but the TV
 * no longer offers or accepts a route into that mode. */
export type TvPresentationSection =
  | 'target'
  | 'challenge'
  | 'contour'
  | 'profile'
  | 'settings';

export type TvRequestedCommand =
  | { kind: 'navigate'; destination: TvNavigationDestination }
  | { kind: 'menu-select'; menuId: string; optionId: string }
  | { kind: 'set-stimp'; stimpFt: number };

export type TvReceiverCommand = TvRequestedCommand & {
  id: string;
  sequence: number;
  issuedAtMs: number;
  expiresAtMs: number;
};

export interface TvSnapshot {
  /** Sound effects (sfx.ts): whether the phone handed sound to this TV, the
   *  player's switches, and the latest cheer. Absent from older phones. */
  sound?: import('../../../packages/putt-scene/src/sound').TvSoundState | null;
  /** Developer Tools, TV diagnostics: what the phone produced for this
   *  publish (packages/putt-scene/src/tvDiagnostics.ts). Absent normally. */
  diag?: import('../../../packages/putt-scene/src/tvDiagnostics').TvProducerDiag | null;
  screen?: string;
  section?: TvPresentationSection;
  routeName?: string | null;
  mode?: string | null;
  title?: string;
  subtitle?: string;
  playerName?: string | null;
  targetFt?: number | null;
  surfaceStimp?: number;
  gateConnected?: boolean;
  /** Publishing gap: the phone's LandscapeStatusPill label when it says more
   * than connected/asleep/disconnected/low battery: WAIT, CHECKING, CHECK
   * GATE, UPDATE GATE, GSPRO READY, GSPRO · WAIT, SCANNING, BT OFF. App
   * source: LandscapeStatusPill's label and tone. The TV pill shows it when
   * present and falls back to `gateConnected` and the notice otherwise. */
  statusPill?: { label: string; tone?: 'ready' | 'wait' | 'sleep' | 'error' | 'battery' } | null;
  /** Receiver-owned health flag; publishers may omit it. */
  tvLinkFresh?: boolean;
  scene?: TvScene | null;
  game?: TvGame | null;
  /** Phone acknowledgement for the latest receiver navigation intent. */
  commandAckId?: string | null;
  commandAckSequence?: number | null;
  /** True only after the phone's exact-document command listener is live. */
  controllerCommandReady?: boolean;
  controllerCommandReadyAtMs?: number;
  /** Round-summary card mirrored from the phone's summary screens. Shape is
   * duplicated in the app's src/tv/summary.ts — keep the two in sync. */
  summary?: TvSummary | null;
  /** Active-round interstitial mirrored from the phone. Unlike a summary,
   * this is a temporary step inside play: next player, between-hole reveal,
   * scorecard, or save/result beat. */
  transition?: TvTransition | null;
  /** An interrupt on the phone that the TV should say out loud (2026-09-25
   * mode map, "Interrupts"): gate asleep or disconnected, a gate hit waiting
   * for confirmation, a read error, a quit or forfeit confirm, a firmware
   * notice, a low battery, a players panel open. `needsPhone` means the
   * player must pick the phone up; the TV then shows "on your phone". */
  notice?: TvNotice | null;
  /** The phone's current menu published as data (src/tv/menuMirror.ts) —
   * The TV shows it as a card (MenuMirror); the phone makes the choice. */
  menu?: TvMenuSpec | null;
  /** What the TV should show (TV_REVIVAL_PLAN.md §2.2). 'course' is the 3D
   * green plus the mode HUD, 'mirror' the phone's current menu as a card,
   * 'card' a summary or transition. Absent means ambient. 'replay' is a
   * retired value an older app may still publish; the page treats it as
   * 'course' when a scene is present. */
  presentation?: {
    kind: 'mirror' | 'course' | 'card' | 'replay';
    routeId?: string | null;
    revision?: number;
    inputOwner?: 'phone' | 'tv' | 'shared';
  };
  /** Sleep policy of the phone's current screen (§2.3). 'touch' means the
   * player must tap the phone; the TV shows "Choose on your phone." */
  sleep?: 'putting' | 'touch';
  /** False while the phone's screen is off during a putting state. The TV
   * shows nothing for it (Cole, 2026-09-26: the old corner hint was noise). */
  phoneAwake?: boolean;
  camera?: {
    phase: 'off' | 'arming' | 'buffering' | 'saving' | 'transferring' | 'ready' | 'error';
    cameraId?: string;
    rollingBufferMs?: number;
  };
  replay?: {
    puttEventId: string;
    clipId?: string;
    status: 'capturing' | 'encoding' | 'transferring' | 'ready' | 'failed';
    impactOffsetMs?: number | null;
    /** Short-lived download URL for the uploaded clip (status 'ready'). */
    url?: string;
    /** Wall-clock length of the uploaded segment. */
    durationMs?: number;
    /** Phone-judged result caption, e.g. "HOLED" / "2.1 FT". */
    label?: string;
  };
}

export type TvHudTone = 'gold' | 'success' | 'danger' | 'neutral';

export interface TvMenuOption {
  id: string;
  label: string;
  detail?: string;
  emphasis?: 'primary' | 'selected' | 'default';
  disabled?: boolean;
}

export interface TvMenuSpec {
  id: string;
  heading?: string;
  options: TvMenuOption[];
}

export interface TvSummaryRow {
  label: string;
  value: string;
  tone?: TvHudTone;
}

export interface TvSummary {
  kicker: string;
  title: string;
  headline?: string;
  headlineDetail?: string;
  rows: TvSummaryRow[];
  footer?: string;
  /** Clip(s) to offer, if the phone happened to capture them. See TvReplayCue. */
  replay?: TvReplayCue | null;
  /** A card that advances by itself: the TV draws a ring that empties at
   * `countdownEndsAtMs` (2026-09-25 mode map). */
  countdownEndsAtMs?: number | null;
  countdownLabel?: string | null;
  /** A leaderboard card: rows in rank order, `you` marks the viewer's row. */
  board?: { title: string; rows: Array<{ rank: number; name: string; value: string; you?: boolean }> } | null;
  /** Playing cards under the headline (a poker hand). */
  cards?: Array<{ rank?: string | number; suit?: string } | string> | null;
  /** Several hands side by side (a Poker challenge result). */
  hands?: Array<{
    label: string;
    detail?: string | null;
    cards: Array<{ rank?: string | number; suit?: string } | string>;
    tone?: TvHudTone;
  }> | null;
  /** The phone's own summary screen, laid out for 16:9 (see TvSummarySection).
   * Additive and optional (2026-09-27). */
  sections?: TvSummarySection[] | null;
  /** Relative widths of the section columns (default one column). */
  columns?: number[] | null;
  /** A code to scan, drawn beside the prompt (an event's leaderboard page).
   * Only https://lagking.com/ addresses are drawn. Additive and optional
   * (2026-10-04). */
  qr?: { url: string; label: string } | null;
}

/** The address a summary's QR code may carry: a LagKing page, nothing else. */
export function summaryQrUrl(summary: Pick<TvSummary, 'qr'> | null | undefined): string | null {
  const url = summary?.qr?.url;
  return typeof url === 'string' && url.length <= 200 && url.startsWith('https://lagking.com/') ? url : null;
}

/* ---------------------------------------------------------------------------
 * Full summary layout (2026-09-27, Cole: "We have room on the TV to show all
 * of the post round summary stuff ... Just fit the stuff we've already built
 * on phone."). A summary screen may publish `sections`: the SAME elements its
 * own phone screen draws (same labels, order, and colors), which the TV lays
 * out side by side in `columns` instead of the phone's vertical stack. Only
 * the arrangement changes. Additive and optional: a TV that does not know
 * `sections` keeps drawing the card from kicker/title/headline/rows (which the
 * phone still sends), and a TV that does know them draws the card when a
 * phone sends none.
 *
 * Payload limits (functions/src/handlers/tvDisplay.ts clampTvJson): objects
 * deeper than summary.sections[i].<array>[j].<field> are dropped, arrays keep
 * 80 items, strings 1000 chars, the whole snapshot 24 KB. So no section
 * nests an array inside an array item (grids are flattened row-major).
 * Colors are resolved on the phone (theme hex values), so the TV never keeps
 * its own copy of the palette.
 * ------------------------------------------------------------------------ */

/** Relative type size, mapped to the TV's scale by each receiver. */
export type TvSummaryTextSize = 'eyebrow' | 'caption' | 'body' | 'title' | 'value' | 'hero';

export interface TvSummaryText {
  text: string;
  /** Hex or rgba; default white. */
  color?: string;
  size?: TvSummaryTextSize;
  weight?: 'regular' | 'bold' | 'heavy';
  /** Letter spacing in em. */
  spacing?: number;
  /** A right-aligned value on the same row (label/value rows, e.g. the rank
   *  badge's "THIS ROUND ... #4"). Flat fields: the payload cannot nest. */
  rightText?: string | null;
  rightColor?: string;
  rightSize?: TvSummaryTextSize;
  rightWeight?: 'regular' | 'bold' | 'heavy';
  /** Draw a thin rule here instead of text (the hero card's divider). */
  divider?: boolean;
}

interface TvSummarySectionBase {
  /** Column index into `TvSummary.columns` (default 0). */
  column?: number;
  /** Small muted section title the phone prints above it ("WHERE THEY LANDED"). */
  title?: string;
  /** Draw the phone's card chrome (cardNavy panel). */
  card?: boolean;
  /** Card border color (a winner's gold border). */
  border?: string;
  /** A full-width row above ('top') or below ('bottom') the columns, in
   *  payload order: a header, a scorecard across the screen (2026-09-30).
   *  Additive: a TV that does not know it lays the section in its column. */
  band?: 'top' | 'bottom' | null;
}

/** Lines of text, top to bottom (hero cards, headers, notes). */
export interface TvSummaryTextSection extends TvSummarySectionBase {
  kind: 'text';
  lines: TvSummaryText[];
  align?: 'center' | 'left';
}

/** The Distance Challenge putt-by-putt card. */
export interface TvSummaryPuttsSection extends TvSummarySectionBase {
  kind: 'putts';
  /** "PUTT BY PUTT" / "TARGET 20 FT". */
  header?: { left: string; right: string } | null;
  rows: Array<{
    index: string;
    primary: string;
    secondary?: string | null;
    color: string;
    line: string;
    lineColor: string;
  }>;
}

/** The top-down "curling house" plot (summaryVisuals/curlingHouse). */
export interface TvSummaryHouseSection extends TvSummarySectionBase {
  kind: 'house';
  balls: Array<{
    side: 'challenger' | 'opponent';
    order: number;
    longitudinalMissFt: number;
    lateralMissFt: number;
    color: string;
    halo?: boolean;
  }>;
  rings: Array<{ radiusFt: number; color?: string; fillOpacity?: number }>;
  lateralExaggeration: number;
  headroomFactor: number;
  edgePaddingPx: number;
  showRingLabels: boolean;
  ringLabelPosition: 'right' | 'top';
  minimumExtentFt?: number;
  /** Ring labels: feet times `unitPerFt`, then `unitLabel` ("3 FT"). */
  unitPerFt: number;
  unitLabel: string;
  /** Chrome colors: dashed target line and labels, pin cross, ball outline,
   *  default ring stroke. */
  palette: { line: string; pin: string; outline: string; ring: string };
  /** Caption under the plot ("Gold: you · Blue: @opp · Number = putt"). */
  caption?: TvSummaryText[] | null;
  /** Legend row above the plot: a dot and a label per side ("YOU",
   *  "@KELLY"). The hint ("Numbers are hole numbers") is drawn as a caption
   *  under the plot. */
  legend?: Array<{ color: string; text: string }> | null;
  legendHint?: string | null;
  legendColor?: string;
}

/** Per-hole leaves for two players ("H1 · 20′" over "IN / 1.2"). */
export interface TvSummaryHoleLeavesSection extends TvSummarySectionBase {
  kind: 'holeLeaves';
  /** `b` is omitted for one player (a sent challenge: your leaves only). */
  holes: Array<{ label: string; a: string; b?: string | null }>;
  labelColor: string;
  colorA: string;
  colorB: string;
  dividerColor: string;
}

/** The seven-bucket start-line chart (summaryVisuals/startLineChart). */
export interface TvSummaryStartLinesSection extends TvSummarySectionBase {
  kind: 'startLines';
  angles: Array<number | null>;
  scaleMaxCount?: number | null;
  /** Hex for each tone key the chart uses, plus the empty-bar and axis colors. */
  palette: {
    proximityGood: string;
    proximityMid: string;
    proximityFar: string;
    empty: string;
    count: string;
    axis: string;
    muted: string;
  };
}

/** A ranked list (standings, round results): one entry per player. */
export interface TvSummaryEntriesSection extends TvSummarySectionBase {
  kind: 'entries';
  rows: Array<{
    rank?: string | null;
    rankColor?: string;
    name: string;
    nameColor?: string;
    detail?: string | null;
    detailColor?: string;
    value: string;
    valueColor?: string;
    /** Small caption under the value ("PTS", "FT AVG"). */
    valueLabel?: string | null;
    /** Highlight border (the leader's gold border). */
    border?: string | null;
  }>;
}

/** Side-by-side score boxes ("YOU 4.2 ft vs THEM 5.1 ft"). */
export interface TvSummaryVersusSection extends TvSummarySectionBase {
  kind: 'versus';
  separator: string;
  boxes: Array<{
    label: string;
    handle?: string | null;
    value: string;
    unit?: string | null;
    valueColor: string;
    border?: string | null;
  }>;
}

/** A row of playing cards (a poker hand). */
export interface TvSummaryHandSection extends TvSummarySectionBase {
  kind: 'hand';
  cards: Array<{ rank?: string | number; suit?: string } | string>;
  /** Parallel to `cards`: faded (not in the best five). */
  dim?: boolean[] | null;
  /** Shown instead of cards when there are none ("No cards earned"). */
  empty?: string | null;
  /** Lines above the cards (name and hand) and below them (notes). */
  lines?: TvSummaryText[] | null;
  after?: TvSummaryText[] | null;
}

/** The two-player Sim 9 match scorecard (summaryVisuals/sim9MatchBoard). */
export interface TvSummaryMatchBoardSection extends TvSummarySectionBase {
  kind: 'matchBoard';
  names: string[];
  holes: number[];
  pars: number[];
  /** Strokes per hole for player one and player two (null = unplayed). */
  strokesA: Array<number | null>;
  strokesB: Array<number | null>;
  /** Players three and on (a Sim 9 stroke-play round of up to six), player
   *  by player, each `holes.length` long: the payload cannot nest arrays.
   *  Additive (2026-09-30): an older TV draws the first two rows. */
  strokesMore?: Array<number | null> | null;
  /** Row label color per player (the ball colors). Default `palette.label`. */
  nameColors?: string[] | null;
  palette: {
    label: string;
    hole: string;
    ft: string;
    par: string;
    score: string;
    won: string;
    wonFill: string;
    line: string;
  };
}

/** The Target session's pace & dispersion plot (summaryVisuals/paceDispersionPlot). */
export interface TvSummaryPacePlotSection extends TvSummarySectionBase {
  kind: 'pacePlot';
  points: Array<{ lateralMissFraction: number; paceMissFraction: number; isMake: boolean }>;
  goalFraction: number;
  ellipse?: {
    center: { lateralFraction: number; paceFraction: number };
    semiMajorFraction: number;
    semiMinorFraction: number;
    rotationDeg: number;
  } | null;
  centroid?: { lateralFraction: number; paceFraction: number } | null;
  palette: {
    disc: string;
    ring: string;
    goal: string;
    goalFill: string;
    ellipse: string;
    ellipseFill: string;
    make: string;
    first: string;
    dot: string;
    outline: string;
    label: string;
  };
  footnote?: TvSummaryText | null;
}

/** Stat tiles in a grid (label, value, sub line). */
export interface TvSummaryMetricsSection extends TvSummarySectionBase {
  kind: 'metrics';
  tiles: Array<{
    label: string;
    value: string;
    sub?: string | null;
    labelColor?: string;
    valueColor?: string;
    subColor?: string;
  }>;
  /** Tiles per row (default 2). */
  perRow?: number;
}

/**
 * A game board as a grid of cells, row-major (2026-09-30): Line Four's final
 * 7 x 6 board (discs in each player's color, the winning line ringed) and
 * Turf War's zones (who controls each band and the landed counts).
 */
export interface TvSummaryGridSection extends TvSummarySectionBase {
  kind: 'grid';
  /** Cells per row. */
  columns: number;
  cells: Array<{
    /** Fill color; null or absent = an empty cell. */
    fill?: string | null;
    /** Text in the cell ("2–1"). */
    text?: string | null;
    textColor?: string | null;
    /** Small text under the cell's text ("10–15 FT"). */
    sub?: string | null;
    /** Outline color (the winning line, a sudden-death zone). */
    ring?: string | null;
  }>;
  /** Labels above each column ("A".."G"). */
  columnLabels?: string[] | null;
  /** 'disc' for game pieces, 'box' for zones (default 'box'). */
  shape?: 'disc' | 'box';
  palette: { empty: string; line: string; label: string };
}

export type TvSummarySection =
  | TvSummaryGridSection
  | TvSummaryTextSection
  | TvSummaryPuttsSection
  | TvSummaryHouseSection
  | TvSummaryStartLinesSection
  | TvSummaryEntriesSection
  | TvSummaryVersusSection
  | TvSummaryHandSection
  | TvSummaryMatchBoardSection
  | TvSummaryPacePlotSection
  | TvSummaryMetricsSection
  | TvSummaryHoleLeavesSection;

export interface TvTransitionLine {
  text: string;
  tone?: TvHudTone;
}

export interface TvTransitionStat {
  label: string;
  value: string;
  tone?: TvHudTone;
}

export interface TvTransitionPlayer {
  name: string;
  accent?: string;
  detail?: string;
  badge?: string;
  cards?: Array<{ rank?: string | number; suit?: string } | string>;
}

export interface TvTransitionTable {
  columns: string[];
  rows: Array<{
    label: string;
    accent?: string;
    values: string[];
  }>;
}

/**
 * A putt worth re-watching, referenced by the Bridge's own putt event id.
 *
 * ⚠️ IDS ONLY. This carries no URL and implies no upload: clips are already
 * captured locally by the paired phone and held in the Bridge's clip store, and
 * A receiver resolves the id against a local store at render time. If a clip for the id
 * was never captured, nothing renders. Do not add a url/blob field here — that
 * would turn a local convenience into a video-distribution feature.
 */
export interface TvReplayCue {
  kind: 'representative-miss' | 'baseline-vs-retest';
  puttEventIds: string[];
  label: string;
}

export interface TvTransition {
  modeTitle: string;
  kicker: string;
  title: string;
  headerRight?: string;
  detail?: string;
  lines?: TvTransitionLine[];
  stats?: TvTransitionStat[];
  players?: TvTransitionPlayer[];
  table?: TvTransitionTable;
  actionLabel?: string;
  footer?: string;
  /** Clip(s) to offer, if the phone happened to capture them. */
  replay?: TvReplayCue | null;
  /** Auto-advancing interstitial: ring empties at this time. */
  countdownEndsAtMs?: number | null;
  countdownLabel?: string | null;
}

export interface TvNotice {
  id: string;
  kind: 'gateAsleep' | 'gateDisconnected' | 'gateHit' | 'readError' | 'confirm' | 'firmware' | 'battery' | 'players' | 'forfeit' | 'info';
  text: string;
  detail?: string | null;
  tone?: TvHudTone;
  needsPhone: boolean;
  /** Auto-clears on the TV at this time when set (read errors, 3 s). */
  expiresAtMs?: number | null;
}

export interface TvHudResult {
  label: string;
  detail?: string;
  tone?: TvHudTone;
}

export interface TvHudCard {
  label: string;
  value: string;
  tone?: TvHudTone;
}

export interface TvStaticBall {
  xFt: number;
  zFt: number;
  color: string;
  label?: string;
}

/** A race bar against a reference score (the day's leader, the King, the
 * challenger, the current local leader). Values are average leave in ft. */
export interface TvRaceBar {
  label: string;
  theirAvgFt: number | null;
  yourAvgFt: number | null;
  /** The reference player's leaves so far, when known, for ghost markers. */
  theirLeavesFt?: Array<number | null>;
}

export type TvGame =
  | {
      /** Target mode (2026-09-25 mode map). */
      kind: 'target';
      targetFt: number;
      startLineLabel?: string | null;
      avgProxFt: number | null;
      puttCount: number;
      lastLeaveFt?: number | null;
      /** Random-target mode: which target of the run. Null in fixed mode. */
      random?: { index: number; total: number; puttsPerTarget: number } | null;
    }
  | {
      /** Every five-putt distance round: solo, daily, friend, local, KOTH. */
      kind: 'distance';
      mode: 'solo' | 'daily' | 'friend' | 'local' | 'koth';
      targetFt: number;
      /** Leaves so far, in order; null for a putt that was recorded but not scored. */
      leavesFt: Array<number | null>;
      puttsTotal: number;
      avgProxFt: number | null;
      playerName?: string | null;
      playerColor?: string | null;
      /** "vs @handle" for friend rounds. */
      vsLabel?: string | null;
      race?: TvRaceBar | null;
      /** KOTH: hole index and total. */
      hole?: { index: number; total: number } | null;
      /** Publishing gap: the seat position in a Pass & Play round, "2/4".
       * App source: LocalMultiplayerRoundScreen `turnLabel`/`contextLabel`
       * (`${playerName} · ${i + 1}/${n}`). */
      seatLabel?: string | null;
      /** Publishing gap: the friend race's goal line, "Under 1.23 ft avg to
       * lead · 3 left". App source: FriendChallengeRoundScreen
       * `distanceChaseGoal(...)` passed as KingRaceBars `goal`. */
      distanceGoal?: string | null;
      /** Names the ghost balls in `scene.extraBalls`: today's Daily #1,
       * "@handle · #1", with a dot in their color (2026-10-20). App source:
       * DailyChallengeScreen via DistanceChallengeLandscape `ghosts`. An
       * older page ignores it and draws the balls unnamed. */
      ghostLegend?: { label: string; color: string } | null;
    }
  | {
      kind: 'sim9';
      courseName: string;
      hole: number;
      holes: number;
      par: number;
      requiredFt: number;
      strokes: number;
      status: string;
      scoreLabel: string;
      playerName?: string;
      /* Publishing gaps (TV_REVIVAL_PLAN.md, "Publishing gaps"). The phone
       * draws these while no TV is connected; the TV renders them as soon
       * as the app sends them. */
      /** Active player's ball color in match play. App source:
       * Sim9StrokePlayScreen `ballColors[activePlayer]`, Sim9MatchPlayScreen
       * `playerColors[game.active]` (both already sent as `scene.ballColor`,
       * which the TV uses until this arrives). */
      playerColor?: string | null;
      /** The active player's round to par ("E", "+2") when `status` carries
       * a match sentence instead (Sim9StrokePlayScreen publishes
       * `liveStatusTitle`, e.g. "COLE LEADS", in `status`). App source: the
       * per-player running total in Sim9StrokePlayScreen's HUD. */
      roundToPar?: string | null;
      /** Every player in a match, in seat order: the phone's player chips and
       * the waiting balls' owners. App source: Sim9StrokePlayScreen `names`,
       * `ballColors`, `game.holeStrokes`, the active index; Sim9MatchPlayScreen
       * `playerColors`, `game.active`, its per-player scores. */
      players?: Array<{
        name: string;
        color?: string | null;
        /** Round to par through the holes each has finished. */
        toPar?: number | null;
        /** Round strokes, when to par is not known. */
        total?: number | null;
        /** Strokes on the current hole. */
        strokes?: number | null;
        active?: boolean;
        /** Holed out on the current hole. */
        holed?: boolean;
      }> | null;
    }
  | {
      kind: 'lineFour';
      names: [string, string];
      phase: string;
      activePlayerIndex: 0 | 1;
      state: {
        board: Array<Array<0 | 1 | null>>;
        attemptCount: number;
        winnerIndex: 0 | 1 | null;
        winningCells: Array<{ row: number; column: number }>;
        isDraw: boolean;
        /** The phone's "n/42" counter (LineFourBoard `moveCount`). Already
         * sent inside `state` by LineFourHost; not in the original type. */
        moveCount?: number;
      };
      /** Publishing gap: the last putt's verdict line, "C · 32.1 FT",
       * "MISS · 9.8 FT", "E IS FULL · TURN SPENT". App source: LineFourHost
       * `resolution`, drawn by LineFourBoard's status line. */
      lastResult?: string | null;
    }
  | {
      /** Baseball (app BASEBALL.md, 2026-10-08). Teams are away then home. */
      kind: 'baseball';
      /** The single, double, triple and home-run holes in feet (2026-10-15); absent from older phones. */
      holesFt?: number[];
      /** The bunt cup in feet while a sacrifice bunt is on, else null (2026-10-15); absent from older phones. */
      buntFt?: number | null;
      /** The batting team's stage per hit hole: 0 = 3 ft, 1 = 1.5 ft, 2 = hole it, 3 = unavailable (2026-10-16); absent from older phones. */
      hitStages?: number[];
      names: [string, string];
      /** Team colors as CSS hex, away then home. */
      colors?: [string, string];
      phase: string;
      role: 'batting' | 'fielding';
      putterTeam: 0 | 1;
      inningLabel: string;
      inning: number;
      innings: number;
      half: 'top' | 'bottom';
      outs: number;
      /** First, second, third. */
      bases: [boolean, boolean, boolean];
      runs: [number, number];
      /** Runs per inning. An object, not nested arrays: Firestore refuses those. */
      lineScore?: { away: number[]; home: number[] };
      /** The batter's ball waiting on the fielder. */
      pending?: { kind: 'hit' | 'sacFly' | 'groundDp'; hole: number; leaveFt?: number; /** Bases the hit is worth (4 = home run). */ bases?: number } | null;
      /** The last finished at-bat: 0 = the batter, 1-3 = bases, 4 = scored. */
      lastPlay?: {
        kind: string;
        team: 0 | 1;
        moves: Array<{ from: 0 | 1 | 2 | 3; to: 1 | 2 | 3 | 4 | 'out' }>;
        runs: number;
      } | null;
      /** Counts finished at-bats: a change is a new play to animate. */
      atBats: number;
      swingOff?: boolean;
      winner?: 0 | 1 | null;
      /** What is asked of the putter, or what the last ball did. */
      status?: string | null;
    }
  | {
      /** Football (app FOOTBALL_DESIGN_PROPOSAL.md, 2026-10-16). Teams are away (team 0, ball
       *  first, drives toward the right end zone) then home. Every field after `kind`,
       *  `names` and `scores` may be missing from an older or partial phone: draw what is there
       *  (footballField.ts footballTvView). */
      kind: 'football';
      names: [string, string];
      /** Team colors as CSS hex, away then home. */
      colors?: [string, string];
      scores: [number, number];
      /** The phone's play phase (handoff, putt, resolving, decision, result). */
      phase?: string;
      /** Whose putt or call it is: offense, defense, kick, the 4th-down or try choice, or the final. */
      role?: 'offense' | 'defense' | 'kick' | 'fourthDown' | 'tryChoice' | 'result';
      /** The side with the ball. */
      offense?: 0 | 1;
      /** The side putting or choosing now. */
      putterTeam?: 0 | 1;
      /** "DRIVE 2 OF 3", "OT 1". */
      driveLabel?: string;
      possessions?: number;
      overtime?: number;
      down?: 1 | 2 | 3 | 4;
      togo?: number;
      /** The line of scrimmage from the offense's own goal line (35 = own 35). */
      yl?: number;
      /** "3RD & 4 . OPP 38", "2-PT TRY". */
      downLabel?: string;
      /** The cup distance the defense answers from, or the kick; null on offense (2026-10-17). */
      targetFt?: number | null;
      twoPoint?: boolean;
      /** The plays table on the left (app footballPlayTable): each play with its cup distance
       *  and what it needs now; tone: fresh, seen, hole, gone, answer, dim, kick. */
      table?: Array<{ key: string; label: string; distance: string; need: string; tone: string }>;
      /** "DRIVE 2 OF 3" since possessions replaced the snap clock (2026-10-17; an older phone sends
       *  "SNAPS LEFT 9" and snapsLeft), none in overtime; the half; the last snap of a half (always
       *  false from a possession phone). */
      clockLabel?: string | null;
      half?: number;
      snapsLeft?: number;
      /** Drives left in the half, the current one included (possessions). */
      drivesLeft?: number;
      lastSnapOfHalf?: boolean;
      /** What is asked of the putter, or what the last snap did. */
      status?: string | null;
      /** The last finished snap, for the ball's slide. Yard lines from `team`'s own goal. */
      lastSnap?: {
        team: 0 | 1;
        fromYl: number;
        toYl: number;
        yards: number;
        result: string;
        label: string;
        /** v5: the one-line result, "SHORT PASS · 9 YARDS", "BROKEN UP". */
        line?: string;
        touchdown: boolean;
        turnover: boolean;
        firstDown: boolean;
      } | null;
      /** Changes once per snap or drive change: the ball moves. */
      snapKey?: number;
      /** A field moment worth a beat (touchdown, interception, ...); `key` changes once per moment. */
      moment?: { kind: string; team: 0 | 1; key: number } | null;
      /** A choice only the phone can make (phonePromptModel.ts): "PICK UP YOUR PHONE, <player>, <action>". Any game may send it. */
      phonePrompt?: { player: string; action: string; color?: string } | null;
      /** After a play (2026-10-17): the result, up two seconds ("12 YARD PASS · FIRST DOWN"); `key` changes per play. */
      popup?: { label: string; tone: string; key: number } | null;
      winner?: 0 | 1 | null;
    }
  | {
      /** Darts-Cricket (app DARTS_CRICKET.md, 2026-10-20): 2 to 4 players in
       *  turn order. Read through cricketBoard.ts cricketTvView, which
       *  tolerates any missing field. */
      kind: 'cricket';
      names: string[];
      colors?: string[];
      /** The phone's phase: handoff, putt, resolving, result. */
      phase?: string;
      points?: number[];
      turn?: number;
      dart?: number;
      round?: number;
      rounds?: number | null;
      /** "ROUND 4 OF 15"; null with no round limit. */
      roundLabel?: string | null;
      /** "DART 2 OF 3", the big corner. */
      dartLabel?: string | null;
      /** The marks table: one row per number, each player's marks 0 to 3. */
      table?: Array<{ key: string; label: string; distance: string; marks: number[]; closed: boolean }>;
      status?: string | null;
      turnSummary?: string | null;
      /** After a putt, two seconds: "TRIPLE 18!" with "+36 PTS"; `key` changes per putt. */
      popup?: { label: string; detail?: string | null; tone: string; key: number } | null;
      winner?: number | null;
      draw?: boolean;
      phonePrompt?: { player: string; action: string; color?: string } | null;
      /** By player: stopped mid-game (live play); the board crosses them out. */
      out?: boolean[];
    }
  | {
      kind: 'turfWar';
      names: [string, string];
      phase: string;
      activePlayerIndex: 0 | 1;
      score: [number, number];
      /** Each zone's "10–15" as the putter sees it: a Rookie or Pro turn sends its level's zones (app kidDistances.ts). Older phones send none. */
      zoneLabels?: string[];
      standings?: Array<{
        zoneIndex: number;
        counts: [number, number];
        controllerIndex: 0 | 1 | null;
      }>;
      state: {
        phase: 'regulation' | 'suddenDeath' | 'complete' | string;
        regulationPutts: [number, number];
        shots: Array<{
          playerIndex: 0 | 1;
          zoneIndex: number | null;
          phase?: 'regulation' | 'suddenDeath';
          suddenDeathRound?: number | null;
        }>;
        suddenDeath?: {
          zoneIndex: number;
          round: number;
        } | null;
        winnerIndex?: 0 | 1 | null;
      };
      /** Publishing gap: the last putt's verdict or the instruction line,
       * "30–35 · RED CONTROLS", "MISS · 8.1 FT", "LAND FROM 10–60 FT TO
       * CLAIM TURF". App source: TurfWarHost `resolution`, drawn by
       * TurfWarHud's status line. */
      lastResult?: string | null;
    }
  | {
      kind: 'poker';
      names: string[];
      hole: number;
      holes: number;
      playerName: string;
      distanceFt: number;
      /** Dynamic 2-card and 1-card Poker scoring radii for this hole. */
      closeFt?: number;
      withinFt?: number;
      handCounts: number[];
      activeHand: Array<{ rank?: string | number; suit?: string } | string>;
      /** The phone's native award overlay is not rendered by the TV client,
       * so the app sends the same reveal as additive game state. */
      phase?: 'putt' | 'award';
      awardCards?: Array<{ rank?: string | number; suit?: string } | string> | null;
      awardTitle?: string | null;
      awardStartedAtMs?: number | null;
      awardRevealDelayMs?: number;
      /** A hand the phone's remote asked the TV to show (open-face poker,
       * every hand is public). Null or absent: nothing open (2026-10-06). */
      peek?: { name: string; cards: Array<{ rank?: string | number; suit?: string } | string> } | null;
    }
  | {
      kind: 'startLine';
      /** 'practice' shows the grade hero only; 'streak' (default) the streak rail. */
      mode?: 'practice' | 'streak';
      distanceFt: number;
      streak: number;
      best: number;
      /** Step Back (2026-10-07): the run opens at 5 ft (4 ft before 2026-10-19) and steps back a foot
       * per make; `distanceFt` is the current putt, `farthestFt` how far the
       * run has reached, `bestFarthestFt` the player's best. Additive. */
      stepBack?: boolean;
      farthestFt?: number;
      bestFarthestFt?: number;
      result: TvHudResult | null;
      startLineLabel?: string | null;
      /** Practice grade: PURE, SLIGHT, WIDE, MAJOR and the push/pull direction. */
      grade?: string | null;
      direction?: 'left' | 'right' | null;
      totalRollFt?: number | null;
      /** Publishing gap: LINE HOLES FROM, the longest putt this start line
       * would hole. App source: StartLineLandscape `holeFt` and
       * StartLineStreakLandscape's LINE HOLES FROM cell. */
      holesFromFt?: number | null;
      /** Daily Start Line (app/LagKing/DAILY_START_LINE.md): the day's one
       * attempt, and the leader's streak it races, locked at round start.
       * Null leader = nobody else has played today, so no race is drawn. App
       * source: StartLineStreakLandscape `tvDailyFields`. */
      daily?: boolean;
      leaderStreak?: number | null;
      leaderLabel?: string | null;
    }
  | {
      kind: 'ladder';
      variant: 'solo' | 'duel';
      phase: string;
      playerName?: string;
      names?: [string, string];
      activePlayerIndex?: 0 | 1;
      round?: number;
      scores?: [number, number];
      climbs?: number;
      pot?: number;
      lowFt: number;
      highFt: number;
      targetLineFt: number;
      finishLineFt: number;
      shotCount?: number;
      result?: TvHudResult | null;
      /** Publishing gap: the solo opening card after a short opening
       * ("Try Again · Last attempt: 12.4 ft, need 15 ft"). App source:
       * LadderGreenLandscape `openingPromptKind` and `lastShortOpening`. */
      openingPrompt?: 'hit' | 'tryAgain' | null;
      lastShortOpeningFt?: number | null;
    }
  | {
      /** An event round (the kiosk, 2026-10-05): one entrant's turn at a
       * Distance, Poker, Sim 9, or Start Line game. The phone is the
       * remote; the TV draws the event's name, where the entrant is in the
       * round, the target, and the round's stat cards. */
      kind: 'event';
      eventName: string;
      /** "Distance 2", or the organizer's name for the game. */
      gameLabel: string;
      /** "PRACTICE 1/3", "PUTT 2/5", "HOLE 3/5". */
      header: string;
      /** Practice putts before the scored round; nothing counts. */
      practicing: boolean;
      targetFt: number;
      playerName?: string | null;
      cards: Array<{ label: string; value: string; tone?: 'gold' | 'danger' | null }>;
      /** Poker games: the cards earned so far, shown as they come (2026-10-08). */
      hand?: Array<{ rank?: string | number; suit?: string } | string> | null;
      /** Miss-scored Distance games: the game leader's round to beat and
       * this entrant's leaves so far, for the race bar. Nothing ends early. */
      race?: TvRaceBar | null;
      leavesFt?: Array<number | null>;
      puttsTotal?: number;
    };

export interface TvRolloutBand {
  id: string;
  minFt: number;
  maxFt: number;
  label: string;
  labelOrientation?: 'billboard' | 'flat';
  /** Visual-only label shift toward the player (Turf War sends -4). */
  labelOffsetFt?: number;
  fillColor?: string;
  fillOpacity?: number;
  lineColor?: string;
  /** Only the standing left-rail label, no cross-lines (Football's cup markers, 2026-10-17). */
  labelOnly?: boolean;
  /** Billboard label size multiplier (Football's markers, 2026-10-17). */
  labelScale?: number;
  /** Which rail a label-only marker stands on (default left). */
  labelSide?: 'left' | 'right';
  /** Right-rail distance painted flat on the turf (Line Four before 2026-10-18). */
  rightLabel?: string;
  /** The right-rail label stands upright (Line Four's first distance, 2026-10-19). */
  rightLabelStanding?: boolean;
  /** How far out a standing label stands, ft (default 5.15; Line Four, 2026-10-19). */
  labelRailFt?: number;
  /** Label text color; defaults to lineColor (Line Four's white labels, 2026-10-19). */
  labelColor?: string;
  /** Football transitions (2026-10-17): kept but faded out; with `fadeMs` it fades. */
  hidden?: boolean;
  fadeMs?: number;
  /** Both rails' labels but no cross-lines (Darts-Cricket, 2026-10-20). */
  hideLines?: boolean;
}

export type TvScene =
  | {
      kind: 'putt';
      /** Scene parity (§2.7): the Sim 9 course and hole the phone is drawing.
       * Optional; the page renders its generic green until it carries the
       * same course data. Never rejected. */
      courseId?: string;
      holeId?: string;
      /** The phone's scenery profile for non-Sim 9 modes (stock scenery id). */
      sceneryId?: string;
      targetFt: number;
      stimp: number;
      hideHole: boolean;
      hideFlag: boolean;
      hideProxLabel?: boolean;
      hideDefaultHud?: boolean;
      hideGimme?: boolean;
      gimmeRadiusFt?: number;
      /** Optional higher-value inner scoring ring; Poker supplies this. */
      innerGimmeRadiusFt?: number;
      /** Reference rings centered on the cup: the lag modes' green and yellow
       *  rings sized to the putt, King of the Hill's gold ring at the king's
       *  leave. Optional; an older phone sends none. */
      /** More cups down the putt line, each with an optional ring (Baseball,
       *  2026-10-08). Decoration only; an older phone sends none. */
      /** This replay's ball was holed in the extra cup at this distance. */
      holedAtFt?: number | null;
      /** Football (2026-10-16): the course is the football field at the
       *  game's spot (packages/putt-scene/src/footballScene.ts,
       *  PuttSceneFootballField). Read through normalizeFootballField, so a
       *  malformed value draws the normal green. An older phone sends none;
       *  an older page ignores it and draws its green with the same cups. */
      footballField?: {
        scrimmageYl: number;
        firstDownYl: number | null;
        aheadEndZone: { color: string; name?: string | null };
        behindEndZone: { color: string; name?: string | null };
      } | null;
      /** Darts-Cricket (2026-10-20): the number fans and the bullseye,
       *  as data (packages/putt-scene/src/dartBoard.ts PuttSceneDartBoard).
       *  Read through normalizeDartBoard; an older page draws the band
       *  labels only. */
      dartBoard?: {
        tripleDeg: number;
        doubleDeg: number;
        bands: Array<{ id: string; minFt: number; maxFt: number; tone?: 0 | 1; closed?: boolean }>;
        bull?: { centerFt: number; outerRadiusFt: number; innerRadiusFt: number; closed?: boolean } | null;
      } | null;
      extraCups?: Array<{ id: string; distanceFt: number; ringRadiusFt?: number; color?: string; opacity?: number; lit?: boolean; dashed?: boolean; ringOnly?: boolean; hidden?: boolean; fadeMs?: number; flag?: boolean; flagColor?: string; holeStyle?: 'field' }>;
      cupRings?: Array<{
        id: string;
        radiusFt: number;
        color: string;
        opacity?: number;
        /** Band opacity while the ball is inside the ring. */
        fillOpacity?: number;
        /** Dashed outline (King of the Hill's crown line). An older phone
         *  never sends it; an older page ignores it and draws a solid ring. */
        dashed?: boolean;
        /** Small crowns along the outline (the crown line). */
        crowns?: boolean;
      }>;
      /** The phone's proximity target ('tight' 5/10, 'standard' 8/16,
       *  'relaxed' 10/20 percent of the putt) for leave colors. Absent from
       *  an older phone: standard. */
      proximityPreset?: 'tight' | 'standard' | 'relaxed';
      /** Cup liner color (King of the Hill: gold when the king holed the
       *  putt). Null or absent keeps the white liner. */
      cupLinerColor?: string | null;
      reviewFly?: 'always' | 'never' | 'skipOnMake';
      holdFinalFrame?: boolean;
      targetLineFt: number | null;
      hideTargetLine?: boolean;
      targetLineResultKnown?: boolean;
      targetLinePassed?: boolean | null;
      finishLineFt: number | null;
      finishLinePassed: boolean;
      rolloutBands: TvRolloutBand[];
      ballColor: string;
      cupPopup?: { title: string; titleColor?: string; sub?: string } | null;
      extraBalls?: TvStaticBall[];
      ballLabel?: string | null;
      challengeProgress: Array<number | null> | null;
      avgProxFt: number | null;
      showAvgProx?: boolean;
      status: { label?: string; color?: string } | null;
      /**
       * Forward-compatible stat rail for a 3D host that does not warrant a
       * dedicated game contract yet. Dedicated game kinds win when both are
       * present; this keeps a new phone build useful on an older TV shell.
       */
      hud?: {
        title?: string;
        subtitle?: string;
        cards?: TvHudCard[];
        result?: TvHudResult | null;
      } | null;
    }
  | {
      kind: 'contour';
      stimp: number;
      totalFt: number;
      segments: Array<{ lengthFt: number; slopeDeg: number }>;
    };

export interface TvPuttEvent {
  kind: 'putt';
  id: string;
  createdAtMs: number;
  /** The gate's read, 2 ft past the putter. Drives the replay clock. */
  speedMps: number;
  /** Ball speed off the face (phone builds ≥ 2026-10-12 translate the gate
   * read with the gate's model). Readouts show this when present and fall
   * back to `speedMps` for older phones. */
  ballSpeedMps?: number;
  distanceFt: number;
  angleDeg: number | null;
  proximityFt: number | null;
  targetFt: number | null;
  /** App-owned make verdict for local/computer putts when one is available. */
  hasHoled?: boolean;
  holed?: boolean;
  /** A recorded opponent's putt replayed on the TV (Sim 9 and Poker friend
   * matches, 2026-09-25): rolls in `color` with `playerName` over the ball and
   * never counts as the phone's putt. */
  ghost?: boolean;
  playerName?: string | null;
  color?: string | null;
  /** Publishing gap (TV_REVIVAL_PLAN.md, "Publishing gaps"): the phone's
   * PuttDataStrip prefixes a low-confidence start line with "~"
   * (`angleLowConfidence`, from `angleConfRef` in Sim9PlayScreen and the
   * notification's angle-confidence flag elsewhere). Not sent yet; the TV
   * shows the "~" when it arrives. */
  angleLowConfidence?: boolean;
  /** A sound to play as the roll starts, for a TV that owns sound (sfx.ts):
   *  the miss tone on a MAJOR start line. */
  sfx?: 'miss';
  /** The phone's sound context for this putt (sound.ts TvPuttSoundWire):
   *  whose putt, the cup it follows, its leave. Absent from older phones. */
  sfxPutt?: { own: boolean; cupFt: number | null; leaveFt: number | null };
}

/** One-time field the server writes after `pairTvSession` (and on a token
 * rotation during resume). The TV stores it at once; the next heartbeat
 * clears it. */
export interface TvDeviceTokenIssue {
  token: string;
  deviceId: string;
}

export interface TvSessionDocument {
  protocolVersion: number;
  receiverName: string;
  /** Set when the session was created from a device token or at pairing. */
  deviceId?: string | null;
  ownerUid?: string | null;
  deviceTokenIssue?: TvDeviceTokenIssue | null;
  /** The username of the account this TV is paired to ("Linked to @Cole"). */
  ownerHandle?: string | null;
  receiverCapabilities?: string[];
  controllerCapabilities?: string[];
  pairingCode: string;
  status: 'waiting' | 'paired' | 'closed';
  sequence: number;
  snapshot: TvSnapshot | null;
  event: TvPuttEvent | null;
  receiverCommand?: TvReceiverCommand | null;
  receiverCommandSequence?: number;
  lastControllerSeenAtMs?: number;
  pairingExpiresAtMs: number;
  expiresAtMs: number;
}

export interface CreatedTvSession {
  protocolVersion: number;
  sessionId: string;
  receiverSecret: string;
  pairingCode: string;
  pairingExpiresAtMs: number;
  expiresAtMs: number;
}

/** `resumeTvSession` result: a fresh session for a TV the account already
 * knows. No pairing code; the phone attaches on its own. */
export interface ResumedTvSession {
  protocolVersion?: number;
  sessionId: string;
  receiverSecret: string;
  deviceId: string;
  expiresAtMs: number;
}
